/**
 * RelationshipVisualizer — Interactive relationship graph for Agent Profiler investigations.
 *
 * Uses vis-network (same lib as Cyber GraphCanvas) to render entity connection graphs
 * built from DeHashed breach data, social media associations, and domain intelligence.
 */
import { useState, useEffect, useRef } from "react";
import { Network } from "vis-network";
import { DataSet } from "vis-data";
import {
  ZoomIn, ZoomOut, Maximize2, Download, X,
  GitBranch, Circle, Grid3X3, Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

const NODE_COLORS = {
  PERSON: "#2196F3",
  ORGANISATION: "#4CAF50",
  DOMAIN: "#FFC107",
  EMAIL: "#9C27B0",
  BREACH: "#F44336",
  IP_ADDRESS: "#00BCD4",
  PHONE: "#FF9800",
  USERNAME: "#E91E63",
  SOCIAL_PROFILE: "#00BCD4",
  LOCATION: "#795548",
  ADDRESS: "#607D8B",
  UNKNOWN: "#9E9E9E",
};

const LAYOUTS = [
  { id: "organic", label: "Organic", icon: Settings, desc: "Natural cluster view — best for dense networks" },
  { id: "hierarchical", label: "Hierarchical", icon: GitBranch, desc: "Top-down structure — trace relationships" },
  { id: "circular", label: "Circular", icon: Circle, desc: "Central entity focus — identify key actors" },
  { id: "orthogonal", label: "Orthogonal", icon: Grid3X3, desc: "Structured grid — clean data layout" },
];

export default function RelationshipVisualizer({ graphData, loading }) {
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const [currentLayout, setCurrentLayout] = useState("organic");
  const [selectedNode, setSelectedNode] = useState(null);
  const [error, setError] = useState(null);

  // Parse graph data
  const nodes = graphData?.nodes || [];
  const edges = graphData?.edges || [];
  const stats = graphData?.stats || {};

  // Render or re-render the graph whenever nodes/edges/layout change
  useEffect(() => {
    if (!containerRef.current || nodes.length === 0) return;

    try {
      // Build vis-network data sets
      const visNodes = new DataSet(
        nodes.map((n) => ({
          id: n.id,
          label: n.name?.length > 25 ? n.name.slice(0, 22) + "..." : n.name,
          title: `<div style="padding:8px;font-size:13px"><b>${n.name}</b><br/>Type: ${n.type}${n.is_central ? '<br/><em>Primary Entity</em>' : ''}</div>`,
          color: {
            background: n.color || "#9E9E9E",
            border: n.is_central ? "#000" : "#555",
          },
          size: n.is_central ? 35 : 20,
          borderWidth: n.is_central ? 4 : 2,
          font: { size: 11, face: "Inter, Arial, sans-serif", color: "#333" },
          shape: n.is_central ? "star" : "dot",
          shadow: { enabled: true, size: n.is_central ? 10 : 5 },
        }))
      );

      const visEdges = new DataSet(
        edges.map((e) => ({
          from: e.source,
          to: e.target,
          label: e.relationship?.replace(/_/g, " "),
          title: `<div style="padding:6px;font-size:12px"><b>${e.relationship?.replace(/_/g, " ")}</b><br/>Confidence: ${(e.confidence * 100).toFixed(0)}%</div>`,
          color: { color: "#999", highlight: "#333", hover: "#666" },
          width: Math.max(1, e.confidence * 3),
          dashes: e.confidence < 0.7,
          font: { size: 9, face: "Inter, Arial, sans-serif", color: "#666", strokeWidth: 2, strokeColor: "#fff" },
          smooth: { type: "continuous" },
        }))
      );

      // Layout options
      const layoutOptions = getLayoutOptions(currentLayout, containerRef.current.clientWidth);

      const options = {
        ...layoutOptions,
        interaction: {
          hover: true,
          tooltipDelay: 200,
          navigationButtons: true,
          keyboard: true,
        },
        physics: {
          enabled: true,
          solver: "forceAtlas2Based",
          forceAtlas2Based: {
            gravitationalConstant: -80,
            centralGravity: 0.01,
            springLength: 120,
            springConstant: 0.04,
            damping: 0.4,
          },
          stabilization: { iterations: 150 },
        },
      };

      networkRef.current = new Network(containerRef.current, { nodes: visNodes, edges: visEdges }, options);

      // Event handlers
      networkRef.current.on("click", (params) => {
        if (params.nodes.length > 0) {
          const nodeId = params.nodes[0];
          const nodeData = nodes.find((n) => n.id === nodeId);
          if (nodeData) {
            const connected = edges
              .filter((e) => e.source === nodeId || e.target === nodeId)
              .map((e) => {
                const otherId = e.source === nodeId ? e.target : e.source;
                const otherNode = nodes.find((n) => n.id === otherId);
                return { name: otherNode?.name || otherId, relationship: e.relationship };
              });
            setSelectedNode({ ...nodeData, connections: connected });
          }
        } else {
          setSelectedNode(null);
        }
      });

      networkRef.current.on("doubleClick", () => {
        networkRef.current.fit({ animation: true });
      });

      setError(null);
    } catch (err) {
      console.error("Graph render error:", err);
      setError("Failed to render relationship graph");
    }

    return () => {
      if (networkRef.current) {
        networkRef.current.destroy();
        networkRef.current = null;
      }
    };
  }, [nodes, edges, currentLayout]);

  const getLayoutOptions = (layoutId, width) => {
    const base = {
      nodes: {
        shape: "dot",
        size: 20,
        font: { size: 11, face: "Inter, Arial, sans-serif" },
        borderWidth: 2,
        shadow: true,
      },
      edges: {
        width: 1.5,
        smooth: { type: "continuous" },
      },
    };

    if (layoutId === "hierarchical") {
      return {
        ...base,
        layout: {
          hierarchical: {
            direction: "UD",
            sortMethod: "directed",
            levelSeparation: 150,
            nodeSpacing: 150,
            treeSpacing: 200,
          },
        },
        physics: { enabled: false },
      };
    }

    if (layoutId === "circular") {
      return {
        ...base,
        layout: { randomSeed: 42 },
        physics: {
          enabled: true,
          solver: "repulsion",
          repulsion: { nodeDistance: 200, centralGravity: 0.3, springLength: 200, springConstant: 0.01, damping: 0.3 },
          stabilization: { iterations: 200 },
        },
      };
    }

    if (layoutId === "orthogonal") {
      return {
        ...base,
        layout: { randomSeed: 0 },
        physics: {
          enabled: true,
          solver: "barnesHut",
          barnesHut: { gravitationalConstant: -2000, centralGravity: 0.3, springLength: 150, springConstant: 0.04, damping: 0.09 },
          stabilization: { iterations: 300 },
        },
      };
    }

    // organic (default)
    return {
      ...base,
      physics: {
        enabled: true,
        solver: "forceAtlas2Based",
        forceAtlas2Based: {
          gravitationalConstant: -80,
          centralGravity: 0.01,
          springLength: 120,
          springConstant: 0.04,
          damping: 0.4,
        },
        stabilization: { iterations: 150 },
      },
    };
  };

  const handleZoomIn = () => networkRef.current?.zoomIn(0.2);
  const handleZoomOut = () => networkRef.current?.zoomOut(0.2);
  const handleFit = () => networkRef.current?.fit({ animation: true });

  const handleExportPng = () => {
    if (!containerRef.current) return;
    const canvas = containerRef.current.querySelector("canvas");
    if (canvas) {
      const link = document.createElement("a");
      link.download = "relationship-graph.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-muted/20 rounded-lg border">
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Building relationship graph...</span>
        </div>
      </div>
    );
  }

  if (!nodes.length) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted/10 rounded-lg border border-dashed">
        <p className="text-sm text-muted-foreground">No relationship data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Controls Bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          {LAYOUTS.map((l) => (
            <Button
              key={l.id}
              variant={currentLayout === l.id ? "default" : "outline"}
              size="sm"
              onClick={() => setCurrentLayout(l.id)}
              className="h-8 text-xs gap-1"
              title={l.desc}
            >
              <l.icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{l.label}</span>
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleZoomIn} title="Zoom In"><ZoomIn className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleZoomOut} title="Zoom Out"><ZoomOut className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleFit} title="Fit to View"><Maximize2 className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleExportPng} title="Export as PNG"><Download className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{stats.total_nodes || nodes.length} nodes</span>
        <span>{stats.total_edges || edges.length} connections</span>
        {stats.density > 0 && <span>Density: {stats.density}</span>}
      </div>

      {/* Graph Container + Selected Node Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div
          ref={containerRef}
          className="lg:col-span-3 h-[500px] bg-card rounded-lg border overflow-hidden"
          style={{ position: "relative" }}
        />
        {selectedNode ? (
          <Card className="p-4 text-sm space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-primary">{selectedNode.name}</h4>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSelectedNode(null)}><X className="w-3 h-3" /></Button>
            </div>
            <Badge variant="secondary" className="text-xs">{selectedNode.type}</Badge>
            {selectedNode.is_central && <Badge className="text-xs ml-1">Primary Entity</Badge>}
            <div className="pt-2">
              <p className="text-xs font-medium text-muted-foreground mb-1">Connections ({selectedNode.connections?.length || 0})</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {(selectedNode.connections || []).slice(0, 20).map((conn, i) => (
                  <div key={i} className="text-xs text-muted-foreground border-b border-border pb-1 last:border-0">
                    {conn.name}
                    <span className="text-[10px] block text-muted-foreground/60">{conn.relationship?.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-4 flex items-center justify-center text-sm text-muted-foreground">
            Click a node to view details
          </Card>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(NODE_COLORS).map(([type, color]) => {
          const count = nodes.filter((n) => n.type === type).length;
          if (count === 0) return null;
          return (
            <div key={type} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: color }} />
              <span>{type.replace(/_/g, " ")} ({count})</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
